/**
 * The Courses routes' PATH params, parsed — or `null` when the segment cannot
 * name anything, so the screen renders its not-found state WITHOUT a request
 * (docs/route-loading.md, "Not-found answers": the loader returns early and
 * the component branches before its suspense read).
 *
 * A `lib/` module, not a screen's, because each route's loader needs the same
 * parse and a loader must never import a component module.
 */

/**
 * `$courseId`, `$lectureId`, `$stepId`: mm-be's `<int:…>` converters, so
 * digits only. Stricter than `Number()` or `z.coerce.number()` on purpose —
 * those read "1e3" as 1000, "0x1f" as 31 and " 12" as 12, turning a URL that
 * names nothing into a request for a real course. A leading zero is refused
 * too: `/courses/012` would be a second URL for course 12.
 */
export const parseId = (raw: string): number | null => {
  if (!/^[1-9]\d*$/.test(raw)) return null
  const value = Number(raw)
  return Number.isSafeInteger(value) ? value : null
}

/** `$slug`: Django's `<slug:…>` converter — letters, digits, `-` and `_`. */
export const parseSlug = (raw: string): string | null => (/^[-A-Za-z0-9_]+$/.test(raw) ? raw : null)

/**
 * `$code`: mm-be's `<str:…>` — anything but a slash. Trimmed, since a code
 * pasted from an email often carries a space; blank is no code. `.` and `..`
 * are refused as well: the request path encodes a slash but not a dot, so
 * either would become a dot-segment the URL resolves away, sending the read
 * to a different endpoint.
 */
export const parseCertificateCode = (raw: string): string | null => {
  const code = raw.trim()
  return code === '' || code === '.' || code === '..' || code.includes('/') ? null : code
}
